import { Resource, Project } from '@/types';

export interface MatchSignal {
  key: 'availability' | 'utilisation' | 'role' | 'location' | 'skills' | 'budget';
  label: string;
  score: number;
  weight: number;
  explanation: string;
  isBlocking: boolean;
  /** User-facing metric value shown in the UI. When null the UI shows "—".
   *  `score` continues to drive the weighted ranking algorithm. */
  displayScore?: number | null;
}

export interface AvailablePeriod {
  start: string;
  end: string;
  type: 'full' | 'partial';
}

export interface ConflictDetail {
  conflictingBookingId: string;
  conflictingProjectId: string;
  conflictingProjectName: string;
  allocationPercent: number;
  overlapStart: string;
  overlapEnd: string;
  impactSummary: string;
  canMove: boolean;
  actionTaken: 'none' | 'move' | 'request-move' | 'skipped';
}

export interface ResourceMatchResult {
  resource: Resource;
  overallScore: number;
  tier: 'high' | 'medium' | 'low';
  signals: MatchSignal[];
  availabilityStatus: 'free' | 'partial' | 'conflict';
  availablePeriods: AvailablePeriod[];
  conflictDetails: ConflictDetail | null;
}

export interface SignalWeights {
  availability: number;
  utilisation: number;
  role: number;
  location: number;
  skills: number;
  budget: number;
}

export const DEFAULT_SIGNAL_WEIGHTS: SignalWeights = {
  availability: 35,
  utilisation: 20,
  role: 18,
  location: 12,
  skills: 10,
  budget: 5,
};

export interface RecommendationEngineInput {
  startDate: Date | undefined;
  endDate: Date | undefined;
  requiredRole: string | undefined;
  location: string | undefined;
  requiredSkills: string[];
  /** ID of the project this booking belongs to — used to look up role budget hours */
  projectId: string | null | undefined;
  /** All projects in the workspace — used for budget hours calculation */
  projects: Project[];
  allResources: Resource[];
  existingBookings: import('@/types').Booking[];
  signalWeights?: SignalWeights;
}

export interface RecommendationEngineOutput {
  cleanMatches: ResourceMatchResult[];
  conflictMatches: ResourceMatchResult[];
  computedAt: number;
}

export interface RecommendationFilterState {
  searchQuery: string;
  roleFilter: string | null;
  locationFilter: string | null;
  minMatchScore: number;
  departmentFilter: string | null;
}

export type ConflictAction = 'none' | 'move' | 'request-move' | 'skipped';
export type DialogConflictActionState = Record<string, ConflictAction>;
